import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import TopBar from '../../global/TopBar';
import { 
  CreditCardIcon, 
  TruckIcon, 
  LockClosedIcon, 
  ArrowLeftIcon, 
  ShoppingBagIcon,
  ShieldCheckIcon 
} from '@heroicons/react/24/outline';
import { useCart } from '../CartContext';
import { loadStripe } from '@stripe/stripe-js';
import { EmbeddedCheckoutProvider, EmbeddedCheckout } from '@stripe/react-stripe-js';
import { motion, AnimatePresence } from 'framer-motion';
import { useSelector } from 'react-redux';

const stripePromise = loadStripe('pk_test_51Q5CqECWFSWzf5rNJ6Oqn6R4q2sn0JwUEmFHoc3Fd8WoHOGGwbqMHFOBqr8ux52GUpNm3pd1PO13nYkP6WMepKJ100n2ghTFuY', {
  stripeAccount: 'acct_1Q5D4aCyUXQBpDBX',
});

const Checkout = () => {
  const navigate = useNavigate();
  const { cart } = useCart();
  const [clientSecret, setClientSecret] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const token = useSelector((state) => state.auth.token);

  useEffect(() => {
    if (cart.length > 0) {
      createCheckoutSession();
    }
  }, [cart]);

  const createCheckoutSession = async () => {
    setIsLoading(true);
    const lineItems = cart.map(item => ({
      price_data: {
        currency: 'gbp',
        product_data: {
          name: `${item.name} (${item.selectedSize}, ${item.selectedColor})`,
        },
        unit_amount: Math.round(item.price * 100),
      },
      quantity: item.quantity,
    }));

    try {
      const response = await fetch(`${process.env.REACT_APP_STORE_API_URL}/create-checkout-session`, {
        method: "POST",
        headers: { "Content-Type": "application/json",
          "Api-Name": "store-api",
          "Authorization": `Bearer ${token}`
         },
        body: JSON.stringify({ items: lineItems }),
      });

      if (!response.ok) throw new Error('Failed to create checkout session');
      const data = await response.json();
      setClientSecret(data.clientSecret);
    } catch (error) {
      console.error('Error creating checkout session:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const getTotalPrice = () => {
    return cart.reduce((total, item) => total + item.price * item.quantity, 0).toFixed(2);
  };

  const handleBack = () => {
    navigate(-1);
  };

  const options = {
    clientSecret
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-pri-dark to-sec-dark text-pri-white">
      <TopBar />
      <div className="container mx-auto px-4 py-8">
        {/* Back Button */}
        <motion.button
          onClick={handleBack}
          className="group flex items-center text-pri-yellow hover:text-pri-green transition-colors"
          whileHover={{ x: -5 }}
          whileTap={{ scale: 0.95 }}
        >
          <ArrowLeftIcon className="h-5 w-5 mr-2 transition-transform group-hover:-translate-x-1" />
          Back to Store
        </motion.button>

        {/* Title */}
        <motion.div 
          className="text-center my-8"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <h1 className="text-3xl sm:text-4xl font-bold bg-gradient-to-r from-pri-yellow to-pri-green text-transparent bg-clip-text">
            Secure Checkout
          </h1>
          <p className="text-pri-gray mt-2">Complete your order securely</p>
        </motion.div>

        {/* Main Content */}
        <div className="flex flex-col lg:flex-row gap-8 max-w-7xl mx-auto">
          {/* Order Summary */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
            className="lg:w-5/12"
          >
            <div className="bg-sec-dark/50 backdrop-blur-sm rounded-xl overflow-hidden">
              <div className="p-6 border-b border-pri-gray/10">
                <h2 className="text-xl font-bold flex items-center">
                  <ShoppingBagIcon className="h-5 w-5 mr-2 text-pri-yellow" />
                  Order Summary
                </h2>
              </div>

              <div className="p-6 space-y-4">
                {cart.map((item) => (
                  <div key={item.id} className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                      <img 
                        src={item.image} 
                        alt={item.name}
                        className="w-12 h-12 rounded-lg object-cover"
                      />
                      <div>
                        <p className="font-medium text-pri-white">{item.name}</p>
                        <div className="flex items-center text-sm text-pri-gray mt-0.5">
                          <span>{item.selectedSize}</span>
                          <div 
                            className="w-3 h-3 rounded-full mx-2 border border-pri-gray/20"
                            style={{ 
                              backgroundColor: item.selectedColor.toLowerCase(),
                              boxShadow: 'inset 0 0 0 1px rgba(255,255,255,0.1)'
                            }}
                          />
                          <span>Qty: {item.quantity}</span>
                        </div>
                      </div>
                    </div>
                    <p className="text-pri-yellow font-medium">£{(item.price * item.quantity).toFixed(2)}</p>
                  </div>
                ))}

                <div className="border-t border-pri-gray/10 pt-4 mt-4">
                  <div className="flex justify-between text-pri-gray mb-2">
                    <span>Subtotal</span>
                    <span>£{getTotalPrice()}</span>
                  </div>
                  <div className="flex justify-between text-pri-gray mb-2">
                    <span>Shipping</span>
                    <span className="text-pri-green">Free</span>
                  </div>
                  <div className="flex justify-between text-lg font-bold text-pri-white">
                    <span>Total</span>
                    <span>£{getTotalPrice()}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Delivery Info */}
            <div className="mt-6 bg-sec-dark/50 backdrop-blur-sm rounded-xl p-6">
              <div className="flex items-center space-x-4">
                <TruckIcon className="h-6 w-6 text-pri-yellow" />
                <div>
                  <h3 className="font-semibold text-pri-white">Free Delivery</h3>
                  <p className="text-sm text-pri-gray">Next-day delivery on orders over £30</p>
                </div>
              </div>
            </div>

            {/* Security Badge */}
            <div className="mt-6 bg-sec-dark/50 backdrop-blur-sm rounded-xl p-6">
              <div className="flex items-center space-x-4">
                <ShieldCheckIcon className="h-6 w-6 text-pri-green" />
                <div>
                  <h3 className="font-semibold text-pri-white">Secure Payment</h3>
                  <p className="text-sm text-pri-gray">Your payment information is encrypted</p>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Payment Section */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            className="lg:w-7/12"
          >
            {isLoading ? (
              <div className="flex justify-center items-center h-[600px] bg-sec-dark/50 rounded-xl">
                <motion.div
                  className="w-16 h-16 border-4 border-pri-yellow border-t-transparent rounded-full"
                  animate={{ rotate: 360 }}
                  transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                />
              </div>
            ) : clientSecret && (
              <div className="bg-white rounded-xl overflow-hidden">
                <EmbeddedCheckoutProvider
                  stripe={stripePromise}
                  options={options}
                >
                  <EmbeddedCheckout />
                </EmbeddedCheckoutProvider>
              </div>
            )}
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default Checkout;
